<template>
  <CCard class="mb-3">
    <CCardHeader class="d-flex align-items-center justify-content-between">
      <span>Filtros</span>
      <CButton
        color="light"
        size="sm"
        class="filter-toggle-btn"
        @click="showAdvanced = !showAdvanced"
      >
        <CIcon name="cil-filter" class="mr-1" />
        {{ showAdvanced ? 'Ocultar filtros' : 'Mostrar filtros' }}
      </CButton>
    </CCardHeader>
    <CCardBody>
      <CRow>
        <CCol md="3" sm="6">
          <div role="group" class="form-group">
            <label>Desde</label>
            <v-date-picker
              class="date-picker"
              v-model="startDatePicker"
              locale="es-MX"
              size="sm"
              :popover="{ visibility: 'click' }"
              @input="onStartDateChange"
            />
          </div>
        </CCol>
        <CCol md="3" sm="6">
          <div role="group" class="form-group">
            <label>Hasta</label>
            <v-date-picker
              class="date-picker"
              v-model="endDatePicker"
              locale="es-MX"
              size="sm"
              :popover="{ visibility: 'click' }"
              @input="onEndDateChange"
            />
          </div>
        </CCol>
        <CCol md="3" sm="6">
          <CSelect
            label="Cuenta"
            :value="localFilters.account_id"
            :options="accountOptions"
            @update:value="setValue('account_id', $event)"
          />
        </CCol>
      </CRow>

      <CRow>
        <CCol md="12" class="mb-2">
          <div class="range-chips">
            <button
              v-for="item in rangeChipOptions"
              :key="item.value"
              type="button"
              class="range-chip"
              :class="{ active: localFilters.range === item.value }"
              @click="applyRangeChip(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </CCol>
      </CRow>

      <CRow v-if="showAdvanced">
        <CCol md="4" sm="6">
          <CSelect
            label="Tipo de mensaje"
            :value="localFilters.message_type"
            :options="messageTypeOptions"
            @update:value="setValue('message_type', $event)"
          />
        </CCol>
        <CCol md="4" sm="6">
          <CSelect
            label="Estado"
            :value="localFilters.status"
            :options="statusOptions"
            @update:value="setValue('status', $event)"
          />
        </CCol>
        <CCol md="4" sm="6">
          <CSelect
            label="Origen"
            :value="localFilters.send_origin"
            :options="originOptions"
            @update:value="setValue('send_origin', $event)"
          />
        </CCol>
      </CRow>

      <CRow v-if="showAdvanced">
        <CCol md="3" sm="6">
          <CSelect
            label="Dia de semana"
            :value="localFilters.weekday"
            :options="weekdayOptions"
            @update:value="setValue('weekday', $event)"
          />
        </CCol>
        <CCol md="3" sm="6">
          <CInput
            label="Minimo por franja"
            type="number"
            min="1"
            :value="localFilters.min_sample"
            @update:value="setValue('min_sample', $event)"
          />
        </CCol>
        <CCol md="3" sm="6">
          <CSelect
            label="Metrica del mapa"
            :value="localFilters.metric"
            :options="metricOptions"
            @update:value="setValue('metric', $event)"
          />
        </CCol>
      </CRow>

      <CRow>
        <CCol md="12" class="d-flex justify-content-end">
          <CButton color="secondary" class="mr-2" @click="resetFilters">Limpiar</CButton>
          <CButton color="success" @click="$emit('apply', localFilters)">Aplicar</CButton>
        </CCol>
      </CRow>
    </CCardBody>
  </CCard>
</template>

<script>
import moment from 'moment'
import { DatePicker } from 'v-calendar'

export default {
  name: 'SendTimeReportFilters',
  components: {
    'v-date-picker': DatePicker,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    accounts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localFilters: { ...this.value },
      showAdvanced: false,
      startDatePicker: null,
      endDatePicker: null,
      rangeOptions: [
        { value: 'today', label: 'Hoy' },
        { value: 'last_7_days', label: 'Ultimos 7 dias' },
        { value: 'last_30_days', label: 'Ultimos 30 dias' },
        { value: 'last_90_days', label: 'Ultimos 90 dias' },
        { value: 'custom', label: 'Personalizado' },
      ],
      messageTypeOptions: [
        { value: '', label: 'Todos' },
        { value: 'invitation', label: 'Enviar Invitaciones' },
        { value: 'reminder', label: 'Enviar Recordatorios' },
        { value: 'save_the_date', label: 'Save the Date' },
        { value: 'preinvitation', label: 'Preinvitacion' },
        { value: 'event_reminder', label: 'Recordatorio de Evento' },
        { value: 'message', label: 'Enviar Mensaje' },
        { value: 'table', label: 'Enviar Mesa' },
        { value: 'tickets', label: 'Enviar Tickets' },
      ],
      statusOptions: [
        { value: '', label: 'Todos' },
        { value: 'sent', label: 'Enviado' },
        { value: 'delivered', label: 'Entregado' },
        { value: 'read', label: 'Leido' },
        { value: 'responded', label: 'Respondido' },
        { value: 'failed', label: 'Fallido' },
        { value: 'undelivered', label: 'No entregado' },
      ],
      originOptions: [
        { value: 'all', label: 'Manual y programado' },
        { value: 'manual', label: 'Manual' },
        { value: 'scheduled', label: 'Programado' },
      ],
      weekdayOptions: [
        { value: 0, label: 'Todos' },
        { value: 1, label: 'Lunes' },
        { value: 2, label: 'Martes' },
        { value: 3, label: 'Miercoles' },
        { value: 4, label: 'Jueves' },
        { value: 5, label: 'Viernes' },
        { value: 6, label: 'Sabado' },
        { value: 7, label: 'Domingo' },
      ],
      metricOptions: [
        { value: 'read_rate', label: 'Tasa de lectura' },
        { value: 'response_rate', label: 'Tasa de respuesta' },
        { value: 'avg_time_to_read', label: 'Tiempo promedio hasta lectura' },
        { value: 'avg_time_to_response', label: 'Tiempo promedio hasta respuesta' },
        { value: 'delivery_rate', label: 'Tasa de entrega' },
        { value: 'error_rate', label: 'Tasa de error' },
        { value: 'sent_count', label: 'Mensajes enviados' },
      ],
    }
  },
  computed: {
    accountOptions() {
      const base = [{ value: 0, label: 'Todas' }]
      const mapped = this.accounts.map((item) => ({
        value: Number(item.id),
        label: item.name,
      }))
      return [...base, ...mapped]
    },
    rangeChipOptions() {
      return this.rangeOptions.filter((item) => item.value !== 'custom')
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newValue) {
        this.localFilters = { ...newValue }
        this.syncDatePickersFromFilters(newValue)
      },
    },
  },
  mounted() {
    this.syncDatePickersFromFilters(this.localFilters)

    if (!this.startDatePicker || !this.endDatePicker) {
      this.applyRangeChip(this.localFilters.range || 'last_30_days')
    }
  },
  methods: {
    setValue(key, value) {
      this.localFilters = {
        ...this.localFilters,
        [key]: value,
      }
    },
    resetFilters() {
      const reset = {
        range: 'last_30_days',
        start_date: '',
        end_date: '',
        account_id: 0,
        message_type: '',
        status: '',
        send_origin: 'all',
        weekday: 0,
        min_sample: 1,
        metric: 'read_rate',
      }

      this.localFilters = reset
      this.applyRangeChip('last_30_days')
      this.$emit('apply', this.localFilters)
    },
    syncDatePickersFromFilters(filters) {
      const startRaw = filters && filters.start_date ? String(filters.start_date) : ''
      const endRaw = filters && filters.end_date ? String(filters.end_date) : ''

      this.startDatePicker = startRaw ? moment(startRaw, 'YYYY-MM-DD').toDate() : null
      this.endDatePicker = endRaw ? moment(endRaw, 'YYYY-MM-DD').toDate() : null
    },
    onStartDateChange(value) {
      this.startDatePicker = value || null
      this.setValue('start_date', this.toDateString(value))
      this.setValue('range', 'custom')
    },
    onEndDateChange(value) {
      this.endDatePicker = value || null
      this.setValue('end_date', this.toDateString(value))
      this.setValue('range', 'custom')
    },
    toDateString(value) {
      if (!value) {
        return ''
      }

      return moment(value).format('YYYY-MM-DD')
    },
    applyRangeChip(rangeValue) {
      const now = moment()
      let start = null
      let end = null

      if (rangeValue === 'today') {
        start = now.clone().startOf('day')
        end = now.clone().endOf('day')
      } else if (rangeValue === 'last_7_days') {
        start = now.clone().subtract(6, 'days').startOf('day')
        end = now.clone().endOf('day')
      } else if (rangeValue === 'last_90_days') {
        start = now.clone().subtract(89, 'days').startOf('day')
        end = now.clone().endOf('day')
      } else {
        start = now.clone().subtract(29, 'days').startOf('day')
        end = now.clone().endOf('day')
        rangeValue = 'last_30_days'
      }

      this.startDatePicker = start.toDate()
      this.endDatePicker = end.toDate()

      this.localFilters = {
        ...this.localFilters,
        range: rangeValue,
        start_date: start.format('YYYY-MM-DD'),
        end_date: end.format('YYYY-MM-DD'),
      }
    },
  },
}
</script>

<style scoped>
.date-picker {
  width: 100%;
}

.range-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.range-chip {
  border: 1px solid #b8b39e;
  border-radius: 20px;
  background: #f4efe3;
  color: #5b584f;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  line-height: 1.2;
  transition: all 0.2s ease;
}

.range-chip:hover {
  background: #e7e0cd;
  border-color: #9e997f;
}

.range-chip.active {
  background: #929c8b;
  border-color: #929c8b;
  color: #fff;
}

.filter-toggle-btn {
  border: 1px solid #d8d3c3;
  color: #5c5648;
  background: #f7f3ea;
}

</style>
